import { FC, memo } from "react";
import { motion, AnimatePresence } from "motion/react";
import styled from "@emotion/styled";
import { useReasoningHeader } from "./reasoning-group-header.hooks";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon";

interface Props {
  latestThinkingText: string;
  isCollapsed: boolean;
  onClick: () => void;
  isExpandable: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  isProcessing: boolean;
  itemCount: number;
}

/**
 * Header component for thought process with animation support and collapse indicator.
 */
export const ReasoningGroupHeader: FC<Props> = ({
  latestThinkingText,
  isCollapsed,
  isExpandable,
  isFirstInGroup,
  isLastInGroup,
  isProcessing,
  itemCount,
  onClick,
}) => {
  return (
    <Wrapper
      isFirstInGroup={isFirstInGroup}
      isLastInGroup={isLastInGroup}
      isCollapsed={isCollapsed}
    >
      <AnimatedHeaderContent
        itemCount={itemCount}
        isExpandable={isExpandable}
        latestThinkingText={latestThinkingText}
        isCollapsed={isCollapsed}
        isProcessing={isProcessing}
        onClick={onClick}
      />
    </Wrapper>
  );
};

// hooks extracted to reasoning-group-header.hooks.ts

/**
 * Animated header content component with crossfade effect.
 */
const AnimatedHeaderContent: FC<{
  isExpandable: boolean;
  latestThinkingText: string;
  isCollapsed: boolean;
  isProcessing: boolean;
  itemCount: number;
  onClick: () => void;
}> = ({ isExpandable, latestThinkingText, isCollapsed, isProcessing, itemCount, onClick }) => {
  const { displayedText, animationMs, shouldShowChevron } = useReasoningHeader({
    itemCount,
    latestThinkingText,
    isProcessing,
    isCollapsed,
  });

  const chevronIcon = isCollapsed ? MDSIconKind.AngleRight : MDSIconKind.AngleDown;

  /**
   * Note - the animation we have is that the thinking text "fades" from one message into another.
   * In order to do this, we use `absolute` styling within motion's `AnimatePresence` component.
   *
   * Motion renders both the "old" and "new" text in the exact same spot, and starts fading out one while
   * fading in the other. (This ensures that they both don't cause the UI to bump around!)
   */
  return (
    <AnimatePresence>
      <motion.span
        key={displayedText}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          duration: animationMs / 1000,
          ease: "easeInOut",
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
          position: "absolute",
          left: 0,
          top: 0,
          whiteSpace: "nowrap",
        }}
      >
        <AnimatedContent
          isExpandable={isExpandable}
          displayedText={displayedText}
          shouldShowChevron={shouldShowChevron}
          chevronIcon={chevronIcon}
          onClick={onClick}
        />
      </motion.span>
    </AnimatePresence>
  );
};

const AnimatedContent: FC<{
  isExpandable: boolean;
  displayedText: string;
  shouldShowChevron: boolean;
  chevronIcon: MDSIconKind;
  onClick: () => void;
}> = memo(({ isExpandable, displayedText, shouldShowChevron, chevronIcon, onClick }) => {
  return (
    <StyledHeader isExpandable={isExpandable} onClick={onClick}>
      {displayedText}
      {shouldShowChevron && (
        <MDSIcon kind={chevronIcon} boxSize={10} size={10} color={MDSIconColor.Secondary} />
      )}
    </StyledHeader>
  );
});

const Wrapper = styled.div<{
  isCollapsed: boolean;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}>`
  position: relative;
  display: block;
  height: 20px;
  margin-top: ${({ isFirstInGroup, theme }) => (isFirstInGroup ? theme.spacing["2"] : "initial")};
  margin-bottom: ${({ isCollapsed, isLastInGroup, theme }) => {
    if (!isCollapsed) {
      return "initial";
    }

    return isLastInGroup ? theme.spacing["4"] : "initial";
  }};
`;

const StyledHeader = styled.div<{
  isExpandable: boolean;
}>`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  cursor: ${({ isExpandable }) => (isExpandable ? "pointer" : "text")};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  gap: 2px;

  user-select: ${({ isExpandable }) => (isExpandable ? "none" : "auto")};

  &:hover {
    text-decoration: ${({ isExpandable }) => (isExpandable ? "underline" : "none")};
  }
`;
